/**
 * Print maps are a PRO/PRO+ feature. The hike overview page has always gated its
 * "Print map" button on the entitlement (entrypoints/hikes.ts), but the map
 * options dialog opened the print page for anyone — the paywall only appeared
 * later, at submit time, after the user had laid sheets out. This is the single
 * gate those surfaces share so the decision (and its destination) can't drift
 * apart per call site again.
 */
import { RevenueCatWrapper } from '../RevenueCatWrapper';

/** Where a non-entitled user is sent instead of the print page. */
export const PRINT_MAP_UPGRADE_URL = '/purchases';

export interface PrintMapAccessDecision {
  /** True when the print page may be opened. */
  allowed: boolean;
  /** Where to send the user instead, or null when allowed. */
  redirectTo: string | null;
}

/**
 * The decision on its own, with no RevenueCat or window dependency so it can be
 * asserted directly. `isPro` covers PRO and PRO+ alike; anonymous users read as
 * false, which is why there is no separate signed-out branch — they need the
 * subscription either way, and /purchases is where they get it.
 */
export function printMapAccessDecision(isPro: boolean): PrintMapAccessDecision {
  return isPro
    ? { allowed: true, redirectTo: null }
    : { allowed: false, redirectTo: PRINT_MAP_UPGRADE_URL };
}

/**
 * Entitlement, resolved once per page. Two of the print entry points open the
 * print page with window.open(..., '_blank'), and a popup only survives the
 * blocker while the click's user activation is still live — so the answer has to
 * be there already rather than a fresh network round trip per click. Pages that
 * look the entitlement up during setup seed it here; anything else falls back to
 * asking RevenueCat.
 */
let entitlement: Promise<boolean> | null = null;

/** Seed the cache from a lookup the page has already paid for. */
export function setPrintMapEntitlement(isPro: boolean): void {
  entitlement = Promise.resolve(isPro);
}

export function printMapEntitlement(): Promise<boolean> {
  if (!entitlement) {
    entitlement = RevenueCatWrapper.isProOrProPlus();
  }
  return entitlement;
}

/** Drop the cached answer (tests, and anything that changes the subscription). */
export function resetPrintMapEntitlement(): void {
  entitlement = null;
}

/** How the gate leaves the page. Injectable because jsdom's location is read-only. */
export type Navigate = (url: string) => void;

function browserNavigate(url: string): void {
  if (typeof window !== 'undefined') {
    window.location.href = url;
  }
}

/**
 * Checks the entitlement and, when it's missing, sends the user to the upgrade
 * page. Returns whether the caller may go on to open the print page.
 */
export async function ensurePrintMapAccess(navigate: Navigate = browserNavigate): Promise<boolean> {
  const decision = printMapAccessDecision(await printMapEntitlement());

  if (!decision.allowed && decision.redirectTo) {
    navigate(decision.redirectTo);
  }

  return decision.allowed;
}
